'use client';

import Link from 'next/link';
import SegmentedToggle from '@/components/SegmentedToggle';
import { useTheme } from '@/contexts/ThemeContext';
import {
  IBusinessTypeIndexEntry,
  IFeatureIndexEntry,
  IProductIndexEntry,
  ToggleOption,
} from '@/interfaces';
import { Language, translations } from '@/lib/translations';
import { usePathname } from 'next/navigation';
import { ctaClick } from '@/lib/analytics';
import { useEffect, useMemo, useState } from 'react';

interface HeaderProps {
  lang: Language;
  businessTypes: IBusinessTypeIndexEntry[];
  products: IProductIndexEntry[];
  features: IFeatureIndexEntry[];
}

export default function Header({
  lang,
  businessTypes,
  products,
  features,
}: HeaderProps) {
  const t = translations[lang];
  const pathname = usePathname();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileAccordion, setMobileAccordion] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const { theme, setTheme } = useTheme();

  // Handle scroll detection
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Theme toggle options for mobile
  const themeOptions: ToggleOption[] = [
    {
      value: 'light',
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
      ),
      ariaLabel: lang === 'en' ? 'Light mode' : '亮色模式',
    },
    {
      value: 'dark',
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
          />
        </svg>
      ),
      ariaLabel: lang === 'en' ? 'Dark mode' : '暗色模式',
    },
    {
      value: 'system',
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
      ),
      ariaLabel: lang === 'en' ? 'Auto mode' : '自動模式',
    },
  ];

  // Helper function to check if a path is active
  const isActivePath = (path: string) => {
    if (path === `/${lang}/home/`) {
      return pathname === `/${lang}/` || pathname === `/${lang}/home/`;
    }
    // Normalize both paths by removing trailing slashes for comparison
    const normalizedPathname = pathname.endsWith('/')
      ? pathname.slice(0, -1)
      : pathname;
    const normalizedPath = path.endsWith('/') ? path.slice(0, -1) : path;
    return normalizedPathname.startsWith(normalizedPath);
  };

  // Helper to scroll to contact form and focus first field
  const handleContactClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    location = 'header'
  ) => {
    const formSection = document.getElementById('contact-form');
    ctaClick(
      'contact_us',
      location,
      lang,
      formSection ? '#contact-form' : e.currentTarget.href
    );
    // If form exists on current page, scroll to it
    if (formSection) {
      e.preventDefault();
      const firstInput = document.getElementById('brandName');
      formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => {
        firstInput?.focus();
      }, 500); // Wait for scroll to complete
    }
    // Otherwise, allow normal navigation to /contact/ page
  };

  // Helper function to get nav item classes
  const getNavClasses = (
    path: string,
    hasDropdown = false,
    dropdownKey?: string
  ) => {
    const baseClasses = `px-3 transition-colors font-medium ${
      isScrolled ? 'py-1 text-xs' : 'py-2 text-sm'
    }`;
    const dropdownClasses = hasDropdown ? 'flex items-center' : '';

    // Check if this dropdown is currently open
    const isDropdownOpen = dropdownKey && openDropdown === dropdownKey;

    if (isActivePath(path)) {
      return `${baseClasses} ${dropdownClasses} text-[var(--link-text)]`;
    } else if (isDropdownOpen) {
      return `${baseClasses} ${dropdownClasses} text-[var(--link-text)]`;
    }
    return `${baseClasses} ${dropdownClasses} text-[var(--color-foreground)] hover:text-[#3880ff]`;
  };

  // Dynamic product dropdown from JSON
  const productDropdown = useMemo(() => {
    return products
      .sort((a, b) => {
        const aOrder = a.order_id ?? 999;
        const bOrder = b.order_id ?? 999;
        return aOrder - bOrder;
      })
      .map(product => ({
        name: lang === 'en' ? product.title_en : product.title_cn,
        href: `/${lang}/products/${product.slug}/`,
        description:
          lang === 'en' ? product.introduction_en : product.introduction_cn,
      }))
      .concat({
        name: lang === 'en' ? 'ChatGPT Integration' : 'ChatGPT 整合',
        href: `/${lang}/chatgpt/`,
        description:
          lang === 'en'
            ? 'Ask about your business and approve supported changes in ChatGPT'
            : '在 ChatGPT 查詢商家資料，並核准支援的變更',
      });
  }, [products, lang]);

  // Dynamic features dropdown from JSON
  const featuresDropdown = useMemo(() => {
    return features.map(feature => ({
      name: lang === 'en' ? feature.title_en : feature.title_cn,
      href: `/${lang}/features/${feature.slug}/`,
      description:
        lang === 'en' ? feature.introduction_en : feature.introduction_cn,
    }));
  }, [features, lang]);

  const successStoriesDropdown = useMemo(() => {
    const items = [
      {
        name: lang === 'en' ? 'Testimonials' : '客戶見證',
        href: `/${lang}/success-stories/`,
        description:
          lang === 'en' ? 'Real customer success stories' : '真實客戶成功案例',
        category: 'main',
      },
      {
        name: lang === 'en' ? 'Branded Apps' : '品牌應用程式',
        href: `/${lang}/branded-apps/`,
        description:
          lang === 'en'
            ? 'Client branded mobile applications'
            : '客戶品牌手機應用程式',
        category: 'main',
      },
    ];

    // Add business types organized by category
    businessTypes.forEach(businessType => {
      const category = businessType.category_en.toLowerCase();

      items.push({
        name:
          lang === 'en'
            ? businessType.short_name_en || businessType.title_en
            : businessType.short_name_cn || businessType.title_cn,
        href: `/${lang}/business-types/${businessType.slug}/`,
        description:
          lang === 'en'
            ? businessType.introduction_en
            : businessType.introduction_cn,
        category: category,
      });
    });

    // Add footer item
    items.push({
      name: lang === 'en' ? 'All Business Types' : '所有業務類型',
      href: `/${lang}/business-types/`,
      description:
        lang === 'en' ? 'View all industry solutions' : '查看所有行業解決方案',
      category: 'main',
    });

    return items;
  }, [businessTypes, lang]);

  const handleMouseEnter = (dropdown: string) => {
    setOpenDropdown(dropdown);
  };

  const handleMouseLeave = () => {
    setOpenDropdown(null);
  };

  return (
    <header
      className={`fixed left-0 right-0 z-40 transition-all duration-300 ${
        isScrolled ? 'shadow-md' : 'shadow-sm'
      }`}
      style={{
        top: 'var(--dev-banner-height, 0)',
        backgroundColor: 'var(--color-card-background)',
        boxShadow: isScrolled ? 'var(--shadow-md)' : 'var(--shadow-sm)',
      }}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`flex justify-between items-center transition-all duration-300 ${
            isScrolled ? 'h-12' : 'h-16'
          }`}
        >
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link href={`/${lang}/home/`} className="flex items-center">
              <span
                className={`font-bold text-[#3880ff] dark:text-blue-400 transition-all duration-300 ${
                  isScrolled ? 'text-base' : 'text-xl'
                }`}
              >
                Omcean
              </span>
              <span
                className={`font-light transition-all duration-300 ${
                  isScrolled ? 'text-base' : 'text-xl'
                }`}
                style={{ color: 'var(--color-foreground-secondary)' }}
              >
                Booking
              </span>
            </Link>
          </div>

          {/* Desktop Navigation - Now shows at lg (1024px) and above */}
          <div className="hidden lg:flex items-center space-x-8">
            {/* Product & Features Combined Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => handleMouseEnter('product-features')}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={`${getNavClasses(`/${lang}/products/`, true, 'product-features')} no-rounded cursor-pointer`}
                style={{
                  color:
                    isActivePath(`/${lang}/products/`) ||
                    isActivePath(`/${lang}/features/`) ||
                    openDropdown === 'product-features'
                      ? 'var(--link-text)'
                      : 'var(--color-foreground)',
                }}
              >
                {lang === 'en' ? 'Product & Features' : '產品與功能'}
                <svg
                  className="ml-1 h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {openDropdown === 'product-features' && (
                <div className="absolute left-0 pt-2 w-96 z-50">
                  <div
                    className="shadow-lg border min-h-[24rem] max-h-[26rem] overflow-hidden"
                    style={{
                      backgroundColor: 'var(--color-card-background)',
                      borderColor: 'var(--color-card-border)',
                      boxShadow: 'var(--shadow-lg)',
                    }}
                  >
                    <div className="max-h-[26rem] overflow-y-auto">
                      <div className="p-4">
                        {/* Overview Section */}
                        <Link
                          href={`/${lang}/products/`}
                          className="block p-3 pl-4 border-l-2 border-transparent hover:border-[#3880ff] transition-all duration-200 mb-2 cursor-pointer hover-bg"
                          onClick={() => setOpenDropdown(null)}
                        >
                          <div
                            className="font-medium text-sm"
                            style={{
                              color: isActivePath(`/${lang}/products/`)
                                ? 'var(--link-text)'
                                : 'var(--color-foreground)',
                            }}
                          >
                            {lang === 'en' ? 'Product Overview' : '產品概覽'}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {lang === 'en'
                              ? 'Explore all our products and solutions'
                              : '探索我們所有的產品和解決方案'}
                          </div>
                        </Link>

                        <Link
                          href={`/${lang}/features/`}
                          className="block p-3 pl-4 border-l-2 border-transparent hover:border-[#3880ff] transition-all duration-200 mb-2 cursor-pointer hover-bg"
                          onClick={() => setOpenDropdown(null)}
                        >
                          <div
                            className="font-medium text-sm"
                            style={{
                              color: isActivePath(`/${lang}/features/`)
                                ? 'var(--link-text)'
                                : 'var(--color-foreground)',
                            }}
                          >
                            {lang === 'en' ? 'Feature Overview' : '功能概覽'}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {lang === 'en'
                              ? 'Discover all our powerful features'
                              : '了解我們所有強大的功能'}
                          </div>
                        </Link>

                        <div
                          className="border-t my-3"
                          style={{ borderColor: 'var(--color-border)' }}
                        ></div>

                        {/* Products Section */}
                        <div className="mb-4">
                          <h4
                            className="text-sm font-bold mb-2 px-3"
                            style={{ color: 'var(--color-foreground)' }}
                          >
                            {lang === 'en' ? 'Products' : '產品'}
                          </h4>
                          <div className="grid grid-cols-2 gap-1">
                            {productDropdown.map(item => (
                              <Link
                                key={item.name}
                                href={item.href}
                                className="block p-2 pl-3 border-l-2 border-transparent hover:border-[#3880ff] transition-all duration-200 cursor-pointer hover-bg"
                                onClick={() => setOpenDropdown(null)}
                              >
                                <div
                                  className="font-normal text-sm"
                                  style={{
                                    color: isActivePath(item.href)
                                      ? 'var(--link-text)'
                                      : 'var(--color-foreground-secondary)',
                                  }}
                                >
                                  {item.name}
                                </div>
                              </Link>
                            ))}
                          </div>
                        </div>

                        {/* Features Section */}
                        <div className="mb-4">
                          <h4
                            className="text-sm font-bold mb-2 px-3"
                            style={{ color: 'var(--color-foreground)' }}
                          >
                            {lang === 'en' ? 'Features' : '功能'}
                          </h4>
                          <div className="grid grid-cols-2 gap-1">
                            {featuresDropdown.map(item => (
                              <Link
                                key={item.name}
                                href={item.href}
                                className="block p-2 pl-3 border-l-2 border-transparent hover:border-[#3880ff] transition-all duration-200 cursor-pointer hover-bg"
                                onClick={() => setOpenDropdown(null)}
                              >
                                <div
                                  className="font-normal text-sm"
                                  style={{
                                    color: isActivePath(item.href)
                                      ? 'var(--link-text)'
                                      : 'var(--color-foreground-secondary)',
                                  }}
                                >
                                  {item.name}
                                </div>
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Pricing - Simple Link */}
            <Link
              href={`/${lang}/pricing/`}
              className={`${getNavClasses(`/${lang}/pricing/`)} cursor-pointer`}
            >
              {lang === 'en' ? 'Pricing' : '價格方案'}
            </Link>

            {/* Interactive Demo */}
            <Link
              href={`/${lang}/demo/`}
              className={`${getNavClasses(`/${lang}/demo/`)} cursor-pointer`}
            >
              {lang === 'en' ? 'Demo' : '體驗'}
            </Link>

            {/* Success Stories Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => handleMouseEnter('success-stories')}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={`${getNavClasses(`/${lang}/success-stories/`, true, 'success-stories')} no-rounded cursor-pointer`}
                style={{
                  color:
                    isActivePath(`/${lang}/success-stories/`) ||
                    openDropdown === 'success-stories'
                      ? 'var(--link-text)'
                      : 'var(--color-foreground)',
                }}
              >
                {lang === 'en' ? 'Success Stories' : '成功案例'}
                <svg
                  className="ml-1 h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {openDropdown === 'success-stories' && (
                <div className="absolute left-0 pt-2 w-96 z-50">
                  <div
                    className="shadow-lg border min-h-[30rem] max-h-[36rem] overflow-hidden"
                    style={{
                      backgroundColor: 'var(--color-card-background)',
                      borderColor: 'var(--color-card-border)',
                      boxShadow: 'var(--shadow-lg)',
                    }}
                  >
                    <div className="max-h-[36rem] overflow-y-auto">
                      <div className="p-4">
                        {/* Testimonials Section */}
                        {successStoriesDropdown
                          .filter(item => item.category === 'main')
                          .map(item => (
                            <Link
                              key={item.name}
                              href={item.href}
                              className="block p-3 pl-4 border-l-2 border-transparent hover:border-[#3880ff] transition-all duration-200 mb-2 cursor-pointer hover-bg"
                              onClick={() => setOpenDropdown(null)}
                            >
                              <div
                                className="font-medium text-sm"
                                style={{ color: 'var(--color-foreground)' }}
                              >
                                {item.name}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {item.description}
                              </div>
                            </Link>
                          ))}

                        <div
                          className="border-t my-3"
                          style={{ borderColor: 'var(--color-border)' }}
                        ></div>

                        {/* Dynamic Categories */}
                        {(() => {
                          // Get unique categories from business types
                          const categories = [
                            ...new Set(businessTypes.map(bt => bt.category_en)),
                          ];

                          return categories.map(categoryEn => {
                            const categoryItems = successStoriesDropdown.filter(
                              item => item.category === categoryEn.toLowerCase()
                            );

                            if (categoryItems.length === 0) return null;

                            // Get the localized category name from the first item with this category
                            const firstItem = businessTypes.find(
                              bt => bt.category_en === categoryEn
                            );
                            const categoryCn =
                              firstItem?.category_cn || categoryEn;

                            return (
                              <div key={categoryEn} className="mb-8">
                                <h4
                                  className="text-sm font-bold mb-2 px-3"
                                  style={{ color: 'var(--color-foreground)' }}
                                >
                                  {lang === 'en' ? categoryEn : categoryCn}
                                </h4>
                                <div className="grid grid-cols-2 gap-1">
                                  {categoryItems.map(item => (
                                    <Link
                                      key={item.name}
                                      href={item.href}
                                      className="block p-2 pl-3 border-l-2 border-transparent hover:border-[#3880ff] transition-all duration-200 cursor-pointer hover-bg"
                                      onClick={() => setOpenDropdown(null)}
                                    >
                                      <div
                                        className="font-normal text-sm"
                                        style={{
                                          color:
                                            'var(--color-foreground-secondary)',
                                        }}
                                      >
                                        {item.name}
                                      </div>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Blog - Simple Link - Only on xl+ */}
            <Link
              href={`/${lang}/blog/`}
              className={`${getNavClasses(`/${lang}/blog/`)} hidden xl:block cursor-pointer`}
            >
              {lang === 'en' ? 'Blog' : '部落格'}
            </Link>

            {/* Resources Dropdown - Only on xl+ */}
            <div
              className="relative hidden xl:block"
              onMouseEnter={() => handleMouseEnter('resources-wide')}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={`${getNavClasses(`/${lang}/help-center/`, true, 'resources-wide')} no-rounded cursor-pointer`}
                style={{
                  color:
                    isActivePath(`/${lang}/help-center/`) ||
                    isActivePath(`/${lang}/faq/`) ||
                    openDropdown === 'resources-wide'
                      ? 'var(--link-text)'
                      : 'var(--color-foreground)',
                }}
              >
                {lang === 'en' ? 'Resources' : '資源'}
                <svg
                  className="ml-1 h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {openDropdown === 'resources-wide' && (
                <div className="absolute left-0 pt-2 w-64 z-50">
                  <div
                    className="shadow-lg border"
                    style={{
                      backgroundColor: 'var(--color-card-background)',
                      borderColor: 'var(--color-card-border)',
                      boxShadow: 'var(--shadow-lg)',
                    }}
                  >
                    <div className="p-4">
                      <Link
                        href={`/${lang}/help-center/`}
                        className="block p-3 pl-4 border-l-2 border-transparent hover:border-[#3880ff] transition-all duration-200 mb-2 cursor-pointer hover-bg"
                        onClick={() => setOpenDropdown(null)}
                      >
                        <div
                          className="font-medium text-sm"
                          style={{ color: 'var(--color-foreground)' }}
                        >
                          {t.help}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {lang === 'en'
                            ? 'Documentation and support'
                            : '文檔和支援'}
                        </div>
                      </Link>
                      <Link
                        href={`/${lang}/faq/`}
                        className="block p-3 pl-4 border-l-2 border-transparent hover:border-[#3880ff] transition-all duration-200 mb-2 cursor-pointer hover-bg"
                        onClick={() => setOpenDropdown(null)}
                      >
                        <div
                          className="font-medium text-sm"
                          style={{ color: 'var(--color-foreground)' }}
                        >
                          {lang === 'en' ? 'FAQ' : '常見問題'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {lang === 'en'
                            ? 'Frequently asked questions'
                            : '常見問題解答'}
                        </div>
                      </Link>
                      <Link
                        href={`/${lang}/who-we-are/`}
                        className="block p-3 pl-4 border-l-2 border-transparent hover:border-[#3880ff] transition-all duration-200 mb-3 cursor-pointer hover-bg"
                        onClick={() => setOpenDropdown(null)}
                      >
                        <div
                          className="font-medium text-sm"
                          style={{ color: 'var(--color-foreground)' }}
                        >
                          {lang === 'en' ? 'Who We Are' : '品牌故事'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {lang === 'en'
                            ? 'Our story and mission'
                            : '我們的故事和使命'}
                        </div>
                      </Link>

                      <Link
                        href={`/${lang}/changelog/`}
                        className="block p-3 pl-4 border-l-2 border-transparent hover:border-[#3880ff] transition-all duration-200 mb-2 cursor-pointer hover-bg"
                        onClick={() => setOpenDropdown(null)}
                      >
                        <div
                          className="font-medium text-sm"
                          style={{ color: 'var(--color-foreground)' }}
                        >
                          {lang === 'en' ? 'Changelog' : '更新紀錄'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {lang === 'en'
                            ? 'Latest product updates'
                            : '最新產品更新'}
                        </div>
                      </Link>

                      <div
                        className="border-t my-3"
                        style={{ borderColor: 'var(--color-border)' }}
                      ></div>

                      <Link
                        href={`/${lang}/uptime/`}
                        className="block p-3 pl-4 border-l-2 border-transparent hover:border-[#3880ff] transition-all duration-200 mb-3 cursor-pointer hover-bg"
                        onClick={() => setOpenDropdown(null)}
                      >
                        <div
                          className="font-medium text-sm"
                          style={{ color: 'var(--color-foreground)' }}
                        >
                          {lang === 'en' ? 'System Status' : '系統狀態'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {lang === 'en'
                            ? 'Real-time system status'
                            : '實時系統狀態'}
                        </div>
                      </Link>

                      <a
                        href={`https://app.omceanbooking.com/business`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-3 pl-4 border-l-2 border-transparent hover:border-[#3880ff] transition-all duration-200 cursor-pointer hover-bg"
                      >
                        <div
                          className="font-medium text-sm"
                          style={{ color: 'var(--color-foreground)' }}
                        >
                          {lang === 'en' ? 'Log in' : '登入'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {lang === 'en'
                            ? 'Access your account'
                            : '訪問您的帳戶'}
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Resources Dropdown - Only on lg (not xl) */}
            <div
              className="relative lg:block xl:hidden"
              onMouseEnter={() => handleMouseEnter('resources')}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={`${getNavClasses(`/${lang}/blog/`, true, 'resources')} no-rounded cursor-pointer`}
                style={{
                  color:
                    isActivePath(`/${lang}/blog/`) ||
                    openDropdown === 'resources'
                      ? 'var(--link-text)'
                      : 'var(--color-foreground)',
                }}
              >
                {lang === 'en' ? 'Resources' : '資源'}
                <svg
                  className="ml-1 h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {openDropdown === 'resources' && (
                <div className="absolute left-0 pt-2 w-64 z-50">
                  <div
                    className="shadow-lg border"
                    style={{
                      backgroundColor: 'var(--color-card-background)',
                      borderColor: 'var(--color-card-border)',
                      boxShadow: 'var(--shadow-lg)',
                    }}
                  >
                    <div className="p-4">
                      <Link
                        href={`/${lang}/blog/`}
                        className="block p-3 pl-4 border-l-2 border-transparent hover:border-[#3880ff] transition-all duration-200 mb-2 cursor-pointer hover-bg"
                        onClick={() => setOpenDropdown(null)}
                      >
                        <div
                          className="font-medium text-sm"
                          style={{ color: 'var(--color-foreground)' }}
                        >
                          {lang === 'en' ? 'Blog' : '部落格'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {lang === 'en'
                            ? 'Latest insights and tips'
                            : '最新見解和技巧'}
                        </div>
                      </Link>
                      <Link
                        href={`/${lang}/who-we-are/`}
                        className="block p-3 pl-4 border-l-2 border-transparent hover:border-[#3880ff] transition-all duration-200 mb-2 cursor-pointer hover-bg"
                        onClick={() => setOpenDropdown(null)}
                      >
                        <div
                          className="font-medium text-sm"
                          style={{ color: 'var(--color-foreground)' }}
                        >
                          {lang === 'en' ? 'Who We Are' : '品牌故事'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {lang === 'en'
                            ? 'Our story and mission'
                            : '我們的故事和使命'}
                        </div>
                      </Link>
                      <Link
                        href={`/${lang}/help-center/`}
                        className="block p-3 pl-4 border-l-2 border-transparent hover:border-[#3880ff] transition-all duration-200 mb-2 cursor-pointer hover-bg"
                        onClick={() => setOpenDropdown(null)}
                      >
                        <div
                          className="font-medium text-sm"
                          style={{ color: 'var(--color-foreground)' }}
                        >
                          {t.help}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {lang === 'en'
                            ? 'Documentation and support'
                            : '文檔和支援'}
                        </div>
                      </Link>
                      <Link
                        href={`/${lang}/faq/`}
                        className="block p-3 pl-4 border-l-2 border-transparent hover:border-[#3880ff] transition-all duration-200 mb-2 cursor-pointer hover-bg"
                        onClick={() => setOpenDropdown(null)}
                      >
                        <div
                          className="font-medium text-sm"
                          style={{ color: 'var(--color-foreground)' }}
                        >
                          {lang === 'en' ? 'FAQ' : '常見問題'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {lang === 'en'
                            ? 'Frequently asked questions'
                            : '常見問題解答'}
                        </div>
                      </Link>
                      <Link
                        href={`/${lang}/changelog/`}
                        className="block p-3 pl-4 border-l-2 border-transparent hover:border-[#3880ff] transition-all duration-200 mb-2 cursor-pointer hover-bg"
                        onClick={() => setOpenDropdown(null)}
                      >
                        <div
                          className="font-medium text-sm"
                          style={{ color: 'var(--color-foreground)' }}
                        >
                          {lang === 'en' ? 'Changelog' : '更新紀錄'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {lang === 'en'
                            ? 'Latest product updates'
                            : '最新產品更新'}
                        </div>
                      </Link>
                      <Link
                        href={`/${lang}/uptime/`}
                        className="block p-3 pl-4 border-l-2 border-transparent hover:border-[#3880ff] transition-all duration-200 mb-3 cursor-pointer hover-bg"
                        onClick={() => setOpenDropdown(null)}
                      >
                        <div
                          className="font-medium text-sm"
                          style={{ color: 'var(--color-foreground)' }}
                        >
                          {lang === 'en' ? 'System Status' : '系統狀態'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {lang === 'en'
                            ? 'Real-time system status'
                            : '實時系統狀態'}
                        </div>
                      </Link>

                      <div
                        className="border-t my-3"
                        style={{ borderColor: 'var(--color-border)' }}
                      ></div>

                      <a
                        href={`https://app.omceanbooking.com/business`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-3 pl-4 border-l-2 border-transparent hover:border-[#3880ff] transition-all duration-200 cursor-pointer hover-bg"
                      >
                        <div
                          className="font-medium text-sm"
                          style={{ color: 'var(--color-foreground)' }}
                        >
                          {lang === 'en' ? 'Log in' : '登入'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {lang === 'en'
                            ? 'Access your account'
                            : '訪問您的帳戶'}
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Mobile/Tablet right side - Contact us button + Hamburger */}
          <div className="flex lg:hidden items-center space-x-2">
            {/* Contact us Button - Always visible */}
            <Link
              href={`/${lang}/contact/`}
              className="bg-blue-700 text-white px-3 py-2 text-sm font-medium hover:bg-[#2563eb] transition-colors rounded-full"
              onClick={event => handleContactClick(event)}
            >
              {lang === 'en' ? 'Contact us' : '聯絡我們'}
            </Link>

            {/* Hamburger menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 transition-colors no-rounded cursor-pointer hover:text-[#3880ff]"
              style={{ color: 'var(--color-foreground)' }}
              aria-label="Toggle mobile menu"
              aria-expanded={mobileMenuOpen}
            >
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {mobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>

          {/* Right side: Desktop - Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* Sign up Button - Only on xl+ (since it's in Resources dropdown on lg) */}
            <a
              href={`https://app.omceanbooking.com/${lang === 'en' ? 'en' : 'zh'}/register`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--color-foreground)] hover:text-[#3880ff] px-4 py-2 text-sm font-medium transition-colors hidden xl:block cursor-pointer"
              onClick={event =>
                ctaClick('sign_up', 'header', lang, event.currentTarget.href)
              }
            >
              {lang === 'en' ? 'Sign up' : '註冊'}
            </a>

            {/* Contact us Button */}
            <Link
              href={`/${lang}/contact/`}
              className="bg-blue-700 text-white px-4 py-2 text-sm font-medium hover:bg-[#2563eb] transition-colors rounded-full"
              onClick={event => handleContactClick(event)}
            >
              {lang === 'en' ? 'Contact us' : '聯絡我們'}
            </Link>
          </div>
        </div>

        {/* Mobile Navigation Drawer - Shows below lg breakpoint */}
        {mobileMenuOpen && (
          <div className="lg:hidden">
            <div
              className="fixed inset-0 z-50"
              onClick={() => setMobileMenuOpen(false)}
            >
              <div
                className="fixed inset-y-0 right-0 max-w-sm w-full shadow-xl border-l"
                style={{
                  backgroundColor: 'var(--color-card-background)',
                  borderColor: 'var(--color-card-border)',
                }}
                onClick={e => e.stopPropagation()}
              >
                <div
                  className="flex items-center justify-between p-4 border-b"
                  style={{ borderColor: 'var(--color-border)' }}
                >
                  <h2
                    className="text-lg font-semibold"
                    style={{ color: 'var(--color-foreground)' }}
                  >
                    Menu
                  </h2>
                  <button
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-1 cursor-pointer"
                  >
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <div className="p-4 space-y-4 overflow-y-auto h-full">
                  {/* Products Accordion */}
                  <div>
                    <button
                      onClick={() =>
                        setMobileAccordion(
                          mobileAccordion === 'products' ? null : 'products'
                        )
                      }
                      className="w-full flex items-center justify-between font-medium hover:text-[#3880ff] text-sm py-2 cursor-pointer"
                      style={{ color: 'var(--color-foreground)' }}
                    >
                      <span>{lang === 'en' ? 'Products' : '產品'}</span>
                      <svg
                        className={`h-4 w-4 transition-transform duration-200 ${mobileAccordion === 'products' ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {mobileAccordion === 'products' && (
                      <div className="mt-2 space-y-2 pl-4">
                        <Link
                          href={`/${lang}/products/`}
                          className="block hover:text-[#3880ff] pl-3 border-l-2 border-transparent hover:border-[#3880ff] text-sm py-1 pr-2 transition-all duration-200 cursor-pointer font-medium hover-bg"
                          style={{ color: 'var(--color-foreground)' }}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {lang === 'en' ? 'All Products' : '所有產品'}
                        </Link>
                        {productDropdown.map(item => (
                          <Link
                            key={item.name}
                            href={item.href}
                            className="block hover:text-[#3880ff] pl-3 border-l-2 border-transparent hover:border-[#3880ff] text-sm py-1 pr-2 transition-all duration-200 cursor-pointer hover-bg"
                            style={{
                              color: 'var(--color-foreground-secondary)',
                            }}
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Features Accordion */}
                  <div>
                    <button
                      onClick={() =>
                        setMobileAccordion(
                          mobileAccordion === 'features' ? null : 'features'
                        )
                      }
                      className="w-full flex items-center justify-between font-medium hover:text-[#3880ff] text-sm py-2 cursor-pointer"
                      style={{ color: 'var(--color-foreground)' }}
                    >
                      <span>{lang === 'en' ? 'Features' : '功能'}</span>
                      <svg
                        className={`h-4 w-4 transition-transform duration-200 ${mobileAccordion === 'features' ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {mobileAccordion === 'features' && (
                      <div className="mt-2 space-y-2 pl-4">
                        <Link
                          href={`/${lang}/features/`}
                          className="block hover:text-[#3880ff] pl-3 border-l-2 border-transparent hover:border-[#3880ff] text-sm py-1 pr-2 transition-all duration-200 cursor-pointer font-medium hover-bg"
                          style={{ color: 'var(--color-foreground)' }}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {lang === 'en' ? 'All Features' : '所有功能'}
                        </Link>
                        {featuresDropdown.map(item => (
                          <Link
                            key={item.name}
                            href={item.href}
                            className="block hover:text-[#3880ff] pl-3 border-l-2 border-transparent hover:border-[#3880ff] text-sm py-1 pr-2 transition-all duration-200 cursor-pointer hover-bg"
                            style={{
                              color: 'var(--color-foreground-secondary)',
                            }}
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Pricing */}
                  <div>
                    <Link
                      href={`/${lang}/pricing/`}
                      className="block font-medium text-[var(--color-foreground)] hover:text-[#3880ff] text-sm py-2 cursor-pointer"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {lang === 'en' ? 'Pricing' : '價格方案'}
                    </Link>
                  </div>

                  {/* Interactive Demo */}
                  <div>
                    <Link
                      href={`/${lang}/demo/`}
                      className="block font-medium text-[var(--color-foreground)] hover:text-[#3880ff] text-sm py-2 cursor-pointer"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {lang === 'en' ? 'Demo' : '體驗'}
                    </Link>
                  </div>

                  {/* Testimonials */}
                  <div>
                    <Link
                      href={`/${lang}/success-stories/`}
                      className="block font-medium text-[var(--color-foreground)] hover:text-[#3880ff] text-sm py-2 cursor-pointer"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {lang === 'en' ? 'Testimonials' : '客戶見證'}
                    </Link>
                  </div>

                  {/* Branded Apps */}
                  <div>
                    <Link
                      href={`/${lang}/branded-apps/`}
                      className="block font-medium text-[var(--color-foreground)] hover:text-[#3880ff] text-sm py-2 cursor-pointer"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {lang === 'en' ? 'Branded Apps' : '品牌應用程式'}
                    </Link>
                  </div>

                  {/* Business Types Accordion */}
                  <div>
                    <button
                      onClick={() =>
                        setMobileAccordion(
                          mobileAccordion === 'business-types'
                            ? null
                            : 'business-types'
                        )
                      }
                      className="w-full flex items-center justify-between font-medium hover:text-[#3880ff] text-sm py-2 cursor-pointer"
                      style={{ color: 'var(--color-foreground)' }}
                    >
                      <span>
                        {lang === 'en' ? 'Business Types' : '業務類型'}
                      </span>
                      <svg
                        className={`h-4 w-4 transition-transform duration-200 ${mobileAccordion === 'business-types' ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {mobileAccordion === 'business-types' && (
                      <div className="mt-2 space-y-2 pl-4">
                        <Link
                          href={`/${lang}/business-types/`}
                          className="block hover:text-[#3880ff] pl-3 border-l-2 border-transparent hover:border-[#3880ff] text-sm py-1 pr-2 transition-all duration-200 cursor-pointer font-medium hover-bg"
                          style={{ color: 'var(--color-foreground)' }}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {lang === 'en'
                            ? 'All Business Types'
                            : '所有業務類型'}
                        </Link>
                        {businessTypes.map(businessType => (
                          <Link
                            key={businessType.slug}
                            href={`/${lang}/business-types/${businessType.slug}/`}
                            className="block hover:text-[#3880ff] pl-3 border-l-2 border-transparent hover:border-[#3880ff] text-sm py-1 pr-2 transition-all duration-200 cursor-pointer hover-bg"
                            style={{
                              color: 'var(--color-foreground-secondary)',
                            }}
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {lang === 'en'
                              ? businessType.short_name_en ||
                                businessType.title_en
                              : businessType.short_name_cn ||
                                businessType.title_cn}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Blog */}
                  <div>
                    <Link
                      href={`/${lang}/blog/`}
                      className="block font-medium hover:text-[#3880ff] text-sm py-2 cursor-pointer"
                      style={{ color: 'var(--color-foreground)' }}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {lang === 'en' ? 'Blog' : '部落格'}
                    </Link>
                  </div>

                  {/* Help Center */}
                  <div>
                    <Link
                      href={`/${lang}/help-center/`}
                      className="block font-medium hover:text-[#3880ff] text-sm py-2 cursor-pointer"
                      style={{ color: 'var(--color-foreground)' }}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t.help}
                    </Link>
                  </div>

                  {/* FAQ */}
                  <div>
                    <Link
                      href={`/${lang}/faq/`}
                      className="block font-medium hover:text-[#3880ff] text-sm py-2 cursor-pointer"
                      style={{ color: 'var(--color-foreground)' }}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {lang === 'en' ? 'FAQ' : '常見問題'}
                    </Link>
                  </div>

                  {/* Changelog */}
                  <div>
                    <Link
                      href={`/${lang}/changelog/`}
                      className="block font-medium hover:text-[#3880ff] text-sm py-2 cursor-pointer"
                      style={{ color: 'var(--color-foreground)' }}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {lang === 'en' ? 'Changelog' : '更新紀錄'}
                    </Link>
                  </div>

                  {/* Theme Toggle */}
                  <div className="border-t border-gray-200 dark:border-gray-600 pt-6 space-y-4">
                    <div className="flex flex-col items-center space-y-3">
                      <SegmentedToggle
                        options={themeOptions}
                        value={theme}
                        onChange={value =>
                          setTheme(value as 'light' | 'dark' | 'system')
                        }
                      />
                    </div>

                    <div className="space-y-3">
                      <a
                        href={`https://app.omceanbooking.com/${lang === 'en' ? 'en' : 'zh'}/register`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-center hover:text-[#3880ff] text-sm font-medium py-2 cursor-pointer"
                        style={{ color: 'var(--color-foreground)' }}
                        onClick={event => {
                          ctaClick(
                            'sign_up',
                            'header_mobile_menu',
                            lang,
                            event.currentTarget.href
                          );
                          setMobileMenuOpen(false);
                        }}
                      >
                        {lang === 'en' ? 'Sign up' : '註冊'}
                      </a>
                      <Link
                        href={`/${lang}/contact/`}
                        className="block bg-blue-700 text-white text-sm font-medium hover:bg-[#2563eb] py-3 text-center transition-colors rounded-full"
                        onClick={e => {
                          setMobileMenuOpen(false);
                          handleContactClick(e, 'header_mobile_menu');
                        }}
                      >
                        {lang === 'en' ? 'Contact us' : '聯絡我們'}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
